class NotificationPromptFocus {
    constructor(root = document) {
        this.root = root;
        this.focusTimer = null;
        this.currentPrompt = null;
        this.observedPrompt = null;
        this.promptWasVisible = false;
        this.promptSelectors = [
            '#onesignal-slidedown-container',
            '.onesignal-slidedown-container',
            '#onesignal-slidedown-dialog',
            '.onesignal-slidedown-dialog',
            '[data-onesignal-slidedown]',
        ];
        this.cancelButtonSelectors = [
            '#onesignal-slidedown-cancel-button',
            '.onesignal-slidedown-cancel-button',
            '.slidedown-button.secondary',
            '[data-onesignal-unique-label*="cancel"]',
        ];
        this.cancelButtonTexts = [
            'ні, дякую',
            'нет, спасибо',
            'no, thanks',
            'no thanks',
        ];
    }

    init() {
        this.listenForOneSignal();
        this.observePrompt();
        this.handlePromptChange();
    }

    listenForOneSignal() {
        window.OneSignalDeferred = window.OneSignalDeferred || [];
        window.OneSignalDeferred.push((OneSignal) => {
            if (!OneSignal || !OneSignal.Slidedown || typeof OneSignal.Slidedown.addEventListener !== 'function') {
                return;
            }

            OneSignal.Slidedown.addEventListener('slidedownShown', (wasShown) => {
                if (wasShown) {
                    this.queueFocus();
                }
            });
        });
    }

    observePrompt() {
        if (!document.body || typeof MutationObserver === 'undefined') {
            return;
        }

        this.domObserver = new MutationObserver((mutations) => {
            const promptMarkupChanged = mutations.some((mutation) => {
                const changedNodes = [...mutation.addedNodes, ...mutation.removedNodes];
                return changedNodes.some((node) => this.containsPromptMarkup(node));
            });

            if (promptMarkupChanged) {
                this.handlePromptChange();
            }
        });
        this.domObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    handlePromptChange() {
        const prompt = this.findPrompt();
        this.watchPrompt(prompt);
        const isVisible = Boolean(prompt && this.isVisible(prompt));
        const hasJustOpened = isVisible && (!this.promptWasVisible || prompt !== this.currentPrompt);

        this.promptWasVisible = isVisible;
        this.currentPrompt = isVisible ? prompt : null;

        if (hasJustOpened) {
            this.queueFocus();
        }
    }

    watchPrompt(prompt) {
        if (prompt === this.observedPrompt) {
            return;
        }

        if (this.promptObserver) {
            this.promptObserver.disconnect();
        }

        this.observedPrompt = prompt;

        if (!prompt || typeof MutationObserver === 'undefined') {
            return;
        }

        this.promptObserver = new MutationObserver(() => this.handlePromptChange());
        this.promptObserver.observe(prompt, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
        });
    }

    containsPromptMarkup(node) {
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return false;
        }

        const selectors = [...this.promptSelectors, ...this.cancelButtonSelectors].join(', ');
        return node.matches(selectors) || Boolean(node.querySelector(selectors));
    }

    queueFocus() {
        window.clearTimeout(this.focusTimer);
        this.focusTimer = window.setTimeout(() => this.focusPrompt(), 0);
    }

    focusPrompt(attempt = 0) {
        const prompt = this.findPrompt();
        const cancelButton = prompt ? this.findCancelButton(prompt) : null;

        if (cancelButton && this.isVisible(cancelButton)) {
            if (!prompt.contains(document.activeElement)) {
                try {
                    cancelButton.focus({preventScroll: true});
                } catch (error) {
                    cancelButton.focus();
                }
            }
            return;
        }

        if (attempt < 15) {
            this.focusTimer = window.setTimeout(() => this.focusPrompt(attempt + 1), 100);
        }
    }

    findPrompt() {
        for (const selector of this.promptSelectors) {
            const prompt = this.root.querySelector(selector);
            if (prompt) {
                return prompt;
            }
        }

        const cancelButton = this.findCancelButton(this.root);
        return cancelButton
            ? cancelButton.closest('[role="dialog"], [id*="onesignal-slidedown"], [class*="onesignal-slidedown"]')
            : null;
    }

    findCancelButton(root) {
        for (const selector of this.cancelButtonSelectors) {
            const button = root.querySelector(selector);
            if (button) {
                return button;
            }
        }

        return Array.from(root.querySelectorAll('button, [role="button"], a')).find((button) => {
            const text = (button.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
            return this.cancelButtonTexts.some((cancelText) => text === cancelText);
        }) || null;
    }

    isVisible(element) {
        const styles = window.getComputedStyle(element);
        const rect = element.getBoundingClientRect();

        return styles.display !== 'none'
            && styles.visibility !== 'hidden'
            && element.getAttribute('aria-hidden') !== 'true'
            && rect.width > 0
            && rect.height > 0;
    }
}

export default NotificationPromptFocus;
